import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { ActivityDashboardService } from './activityDashboardService';

export interface RangeConfig {
    range1: { label: string; upperBound: number };
    range2: { label: string; upperBound: number };
    range3: { label: string };
}

export const DEFAULT_RANGE_CONFIG: RangeConfig = {
    range1: { label: 'Recent (Last 10 Yrs)', upperBound: 10 },
    range2: { label: 'Mid (10–20 Yrs)',      upperBound: 20 },
    range3: { label: 'Senior (20+ Yrs)' },
};

const ALL_PERIODS       = ['monthly', 'quarterly', 'halfyearly', 'annual'];
const ALL_BATCH_RANGES  = ['all', 'range1', 'range2', 'range3'];

interface DashboardState {
    selectedGroupId: number | null;
    selectedGroupName: string;
    selectedInstituteId: number | null;
    selectedInstituteName: string;
    instituteLoaded: boolean;
    timeframe: string;
    compareMode: boolean;
    comparePeriod: string;
    batchRange: string;
    rangeConfig: RangeConfig;
    setRangeConfig: (cfg: RangeConfig) => void;
    summary: any;
    emailCampaigns: any[];
    whatsappStats: any;
    eventActivity: any;
    otherActions: any;
    leaderboard: any[];
    contributions: any;
    loading: boolean;
    setSelectedGroup: (id: number | null, name: string) => void;
    setSelectedInstitute: (id: number | null, name: string) => void;
    setTimeframe: (tf: string) => void;
    setCompareMode: (on: boolean) => void;
    setComparePeriod: (p: string) => void;
    setBatchRange: (r: string) => void;
    refresh: () => void;
}

const DashboardContext = createContext<DashboardState>({} as DashboardState);
export const useDashboard = () => useContext(DashboardContext);

export const DashboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [selectedGroupId, setGroupId]         = useState<number | null>(null);
    const [selectedGroupName, setGroupName]     = useState('');
    const [selectedInstituteId, setInstituteId] = useState<number | null>(null);
    const [selectedInstituteName, setInstituteName] = useState('');
    const [instituteLoaded, setInstituteLoaded] = useState(false);
    const [timeframe, setTimeframe]             = useState('monthly');
    const [compareMode, setCompareMode]         = useState(false);
    const [comparePeriod, setComparePeriod]     = useState('prev');
    const [batchRange, setBatchRange]           = useState('all');
    const [rangeConfig, setRangeConfig]         = useState<RangeConfig>(DEFAULT_RANGE_CONFIG);

    const [summary, setSummary]               = useState<any>(null);
    const [emailCampaigns, setEmailCampaigns] = useState<any[]>([]);
    const [whatsappStats, setWhatsappStats]   = useState<any>(null);
    const [eventActivity, setEventActivity]   = useState<any>(null);
    const [otherActions, setOtherActions]     = useState<any>(null);
    const [leaderboard, setLeaderboard]       = useState<any[]>([]);
    const [contributions, setContributions]   = useState<any>(null);
    const [loading, setLoading]               = useState(false);

    // ── Frontend period cache ─────────────────────────────────────────────────
    // Key: `${period}_${batchRange}_${r1}_${r2}`
    const periodCacheRef = useRef<Map<string, any>>(new Map());

    const cacheKey = useCallback((period: string) =>
        `${period}_${batchRange}_${rangeConfig.range1.upperBound}_${rangeConfig.range2.upperBound}`,
    [batchRange, rangeConfig]);

    const applyData = (d: any) => {
        setSummary(d.summary || null);
        setEmailCampaigns(d.emailCampaigns || []);
        setWhatsappStats(d.whatsappStats || null);
        setEventActivity(d.eventActivity || null);
        setOtherActions(d.otherActions || null);
        setLeaderboard(d.leaderboard || []);
        setContributions(d.contributions || null);
    };

    // ── Fetch one period (with frontend cache check) ──────────────────────────
    const fetchPeriod = useCallback(async (
        period: string, iid: number, showLoading: boolean, r1: number, r2: number, br: string
    ) => {
        const key = `${period}_${br}_${r1}_${r2}`;
        if (periodCacheRef.current.has(key)) {
            if (showLoading) {
                // Instant render from frontend cache — no loading state needed
                applyData(periodCacheRef.current.get(key));
            }
            return;
        }
        if (showLoading) setLoading(true);
        try {
            // getAllData supplies summary cards + Other Actions + Leaderboard (all_activity_logs).
            // The Campaign Interactions section (Email + WhatsApp) comes ONLY from
            // moengage_template via getMoengageCampaigns. Best-effort: on failure we keep
            // the getAllData values so the cards never break.
            const [res, moeRes] = await Promise.all([
                ActivityDashboardService.getAllData(iid, period, br, r1, r2),
                ActivityDashboardService.getMoengageCampaigns(iid, period)
                    .catch(e => { console.error(`[Dashboard] moengage campaigns error (${period}):`, e); return null; }),
            ]);
            const d = res.data?.data || {};
            const moe = moeRes?.data?.data;
            if (moe) {
                if (Array.isArray(moe.emailCampaigns)) d.emailCampaigns = moe.emailCampaigns;
                if (moe.whatsappStats) d.whatsappStats = moe.whatsappStats;
            }
            periodCacheRef.current.set(key, d);
            if (showLoading) applyData(d);
        } catch (err) {
            console.error(`[Dashboard] fetch error (${period}):`, err);
        } finally {
            if (showLoading) setLoading(false);
        }
    }, []);

    // ── Main fetch: load current period + preload others in background ────────
    const fetchAll = useCallback(async () => {
        if (!selectedInstituteId) return;
        const r1 = rangeConfig.range1.upperBound;
        const r2 = rangeConfig.range2.upperBound;
        const key = cacheKey(timeframe);

        if (periodCacheRef.current.has(key)) {
            // Instant: already in frontend cache
            applyData(periodCacheRef.current.get(key));
        } else {
            // Load current period+batchRange with loading indicator
            await fetchPeriod(timeframe, selectedInstituteId, true, r1, r2, batchRange);
        }

        // Background: preload other 3 periods (same batchRange)
        ALL_PERIODS.filter(p => p !== timeframe).forEach(p => {
            fetchPeriod(p, selectedInstituteId, false, r1, r2, batchRange);
        });

        // Background: preload other 3 batch ranges (same period) → instant filter switching
        ALL_BATCH_RANGES.filter(br => br !== batchRange).forEach(br => {
            fetchPeriod(timeframe, selectedInstituteId, false, r1, r2, br);
        });
    }, [selectedInstituteId, timeframe, batchRange, rangeConfig, cacheKey, fetchPeriod]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const setSelectedGroup = (id: number | null, name: string) => {
        setGroupId(id);
        setGroupName(name);
        setInstituteId(null);
        setInstituteName('');
        setInstituteLoaded(false);
    };

    const setSelectedInstitute = (id: number | null, name: string) => {
        // Clear frontend period cache when institute changes
        periodCacheRef.current.clear();
        setInstituteId(id);
        setInstituteName(name);
        setInstituteLoaded(!!id);
        setBatchRange('all');
    };

    const handleSetBatchRange = (r: string) => {
        // No cache clear needed — cache key includes batch range, so each range is isolated
        setBatchRange(r);
    };

    const handleSetRangeConfig = (cfg: RangeConfig) => {
        // Clear frontend period cache when range config changes
        periodCacheRef.current.clear();
        setRangeConfig(cfg);
    };

    const handleSetTimeframe = (tf: string) => {
        setTimeframe(tf);
        setComparePeriod('prev');
        if (tf === 'monthly') setCompareMode(false);
    };

    return (
        <DashboardContext.Provider value={{
            selectedGroupId, selectedGroupName,
            selectedInstituteId, selectedInstituteName, instituteLoaded,
            timeframe, compareMode, comparePeriod, batchRange, rangeConfig,
            setRangeConfig: handleSetRangeConfig,
            summary, emailCampaigns, whatsappStats, eventActivity,
            otherActions, leaderboard, contributions, loading,
            setSelectedGroup, setSelectedInstitute,
            setTimeframe: handleSetTimeframe,
            setCompareMode, setComparePeriod,
            setBatchRange: handleSetBatchRange,
            refresh: fetchAll,
        }}>
            {children}
        </DashboardContext.Provider>
    );
};
